import { Component, ReactNode } from 'react'
import { Box, Button, Card, CardContent, Typography } from '@mui/material'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlineOutlined'
import i18n from '../i18n/config'

interface ErrorBoundaryProps {
  children: ReactNode
  fallback?: ReactNode
}

interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null
    }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // DEV-only. In production the boundary's job is to render a fallback,
    // not to leak stack traces / component trees to the user's devtools.
    // Real monitoring would go through a dedicated service, not console.
    if (import.meta.env.DEV) {
      console.error('ErrorBoundary caught an error:', error, errorInfo)
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null
    })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100dvh',
            p: 3
          }}
        >
          <Card sx={{ maxWidth: 500 }}>
            <CardContent>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center',
                  gap: 2
                }}
              >
                <ErrorOutlineIcon color="error" sx={{ fontSize: 60 }} />

                <Typography variant="h5" component="h1">
                  {i18n.t('errors.boundaryTitle')}
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  {i18n.t('errors.boundaryDescription')}
                </Typography>

                {/* Raw error message is dev-only. In production it leaks
                    implementation details (e.g. "Cannot read properties of
                    null (reading 'length')") to end users — useless to them
                    and a confusing technical artefact. Console + sourcemaps
                    still capture the real error for debugging. */}
                {import.meta.env.DEV && this.state.error && (
                  <Box
                    sx={{
                      mt: 2,
                      p: 2,
                      bgcolor: 'grey.100',
                      borderRadius: 1,
                      width: '100%',
                      overflow: 'auto'
                    }}
                  >
                    <Typography variant="caption" component="pre" sx={{ m: 0 }}>
                      {this.state.error.message}
                    </Typography>
                  </Box>
                )}

                <Button
                  variant="contained"
                  onClick={this.handleReset}
                  sx={{ mt: 2 }}
                >
                  {i18n.t('common.tryAgain')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )
    }

    return this.props.children
  }
}
